import { MutableRefObject, useRef, useState } from "react";
import { useApp } from "../../../context/AppContext";
import { useStaffFormSalon } from "./useStaffFormSalon";
import type {
  DayKey,
  SavedStaffMember,
  StaffFormInitialData,
  StaffFormSaveResult,
  StaffFormSection,
  StaffFormValues,
  StaffServiceAssignments,
} from "../../../types/staffForm";
import {
  buildInvitePayload,
  buildUpdatePayload,
  getServerErrorMessage,
  initialDataFromInvite,
  splitPhoneNumber,
} from "./staffFormUtils";
import type { StaffPhotoChange } from "./useStaffPhoto";
import {
  createdStaffServiceIds,
  diffStaffServices,
  persistedAssignments,
} from "./staffServiceUtils";

export type StaffFormSubmitOutcome =
  | { ok: true; result: StaffFormSaveResult }
  | { ok: false; message: string; section?: StaffFormSection };

interface UseStaffFormSubmitArgs {
  initialData?: StaffFormInitialData | null;
  touchedHourDays: MutableRefObject<Set<DayKey>>;
  userToggledBookable: MutableRefObject<boolean>;
  /** The staged photo change, and how to clear it once saved. */
  photo: {
    change: StaffPhotoChange;
    markSaved: (url: string | null) => void;
  };
  /** The staff member's assignments as currently stored (diff baseline). */
  baselineAssignments: StaffServiceAssignments;
  /** False when the user may not (or the staff can't) have services written. */
  canWriteMenu: boolean;
  /** Called after each successful staff write so the host can update its list. */
  onStaffSaved: (staff: SavedStaffMember, meta: { isCreate: boolean }) => void;
}

/**
 * The invite succeeded but returned no id, so there is nothing to update or
 * attach services to from here.
 */
const MISSING_STAFF_ID_MESSAGE =
  "The staff member was added, but their record couldn't be loaded. Close this window and edit them from the staff list.";

class MissingStaffIdError extends Error {
  constructor() {
    super(MISSING_STAFF_ID_MESSAGE);
    this.name = "MissingStaffIdError";
  }
}

// Matched by name, not instanceof: transpiled Error subclasses can lose their
// prototype chain in older browser targets.
const isMissingStaffIdError = (error: unknown): boolean =>
  (error as Error | null)?.name === "MissingStaffIdError";

/**
 * `POST /services` succeeded but didn't return the new staff_services id for
 * some rows. They exist server-side yet can't be updated or deleted from here,
 * so they're skipped for the rest of the session rather than re-created.
 */
class UnconfirmedStaffServicesError extends Error {
  constructor(readonly count: number) {
    super(`${count} created staff service(s) returned no id`);
    this.name = "UnconfirmedStaffServicesError";
  }
}

const unconfirmedStaffServicesCount = (error: unknown): number | null =>
  (error as Error | null)?.name === "UnconfirmedStaffServicesError"
    ? (error as UnconfirmedStaffServicesError).count
    : null;

/** Everything the staff PUT/invite carries (i.e. all but the Menu). */
const staffSnapshot = ({ serviceAssignments, ...rest }: StaffFormValues) =>
  JSON.stringify(rest);

/** The staff_photo_s3_uri to write: a URL, null to remove, or undefined (no change). */
type PhotoField = { staff_photo_s3_uri: string | null } | undefined;

/**
 * Saves the form in two phases — the staff record (with any staged photo,
 * uploaded first), then the staff_services diff — and survives a failure
 * between them:
 * - an invite is never repeated (the created id is kept for the session);
 * - the staff record is only re-sent when its fields changed since the last
 *   successful write;
 * - service rows that were already written are folded into the baseline, so a
 *   retry neither re-creates nor re-deletes them;
 * - a create whose new id the server didn't return is never re-sent (that
 *   would duplicate the row); the user is told to reopen and check it.
 */
export function useStaffFormSubmit({
  initialData,
  touchedHourDays,
  userToggledBookable,
  photo,
  baselineAssignments,
  canWriteMenu,
  onStaffSaved,
}: UseStaffFormSubmitArgs) {
  const {
    uploadImage,
    inviteStaff,
    updateStaff,
    createServices,
    updateServices,
    deleteStaffService,
  } = useApp();
  const { businessInfo, refreshServices } = useStaffFormSalon();
  const businessId = businessInfo?.id ?? "";
  const isCreate = !initialData?.id;

  const staffIdRef = useRef<string | null>(initialData?.id ?? null);
  /** The record as the server now holds it — drives PUT's "loaded" rules. */
  const savedRecordRef = useRef<StaffFormInitialData | null>(
    initialData ?? null,
  );
  const invitedRef = useRef(false);
  const savedSnapshotRef = useRef<string | null>(null);
  const persistedRef = useRef<StaffServiceAssignments | null>(null);
  const servicesWrittenRef = useRef(false);
  /** salon_service_ids created without a returned id — never re-created. */
  const unconfirmedCreatesRef = useRef<Set<string>>(new Set());
  /** The last staged file already uploaded, so a retry doesn't upload twice. */
  const uploadedPhotoRef = useRef<{ file: File; url: string } | null>(null);
  /** Whether the staff member exists server-side (drives the Save label). */
  const [staffExists, setStaffExists] = useState(!isCreate);

  /**
   * The staged photo as a staff field. A new file is uploaded here (as a plain
   * image, then written via the invite / PUT /staff like any other staff
   * field, so the same permissions apply).
   */
  const resolvePhotoField = async (): Promise<PhotoField> => {
    const { change } = photo;
    if (!change) return undefined;
    if (change.kind === "remove") return { staff_photo_s3_uri: null };
    if (uploadedPhotoRef.current?.file !== change.file) {
      const response = await uploadImage({
        image: change.file,
        business_location_id: businessId,
        optimization_type: "web",
      });
      const url = response?.cloudfront_url ?? response?.s3_url;
      if (!url) throw new Error("The photo upload returned no URL.");
      uploadedPhotoRef.current = { file: change.file, url };
    }
    return { staff_photo_s3_uri: uploadedPhotoRef.current.url };
  };

  /** List patch for a photo change (the list renders staff_photo_url). */
  const photoListPatch = (photoField: PhotoField) =>
    photoField
      ? { staff_photo_url: photoField.staff_photo_s3_uri, avatar_url: null }
      : {};

  const inviteStaffMember = async (
    values: StaffFormValues,
    photoField: PhotoField,
  ) => {
    const response = await inviteStaff(
      buildInvitePayload(values, {
        businessId,
        photoUrl: photoField?.staff_photo_s3_uri,
      }),
    );
    invitedRef.current = true;
    setStaffExists(true);
    const staffId: string | null = response?.sub ?? response?.id ?? null;
    staffIdRef.current = staffId;
    if (staffId) {
      savedRecordRef.current = initialDataFromInvite(values, staffId);
    }
    const { areaCode, phoneNumber } = splitPhoneNumber(values.phone);
    const { salon_id, staff_photo_s3_uri, ...invited } = buildInvitePayload(
      values,
      { businessId },
    );
    onStaffSaved(
      {
        ...invited,
        ...photoListPatch(photoField),
        id: staffId ?? undefined,
        first_name: response?.given_name ?? values.firstName,
        last_name: response?.family_name ?? values.lastName,
        email: response?.email ?? values.email,
        country_code: "+1",
        area_code: areaCode,
        phone_number: phoneNumber,
      } as SavedStaffMember,
      { isCreate: true },
    );
  };

  const updateStaffMember = async (
    values: StaffFormValues,
    photoField: PhotoField,
  ) => {
    const payload = {
      ...buildUpdatePayload(values, {
        staffId: staffIdRef.current as string,
        initialData: savedRecordRef.current,
        businessId,
        touchedDays: touchedHourDays.current,
        userToggledBookable: userToggledBookable.current,
      }),
      ...photoField,
    };
    await updateStaff(payload);
    onStaffSaved(
      { ...payload, ...photoListPatch(photoField) } as SavedStaffMember,
      { isCreate: false },
    );
  };

  const saveStaff = async (values: StaffFormValues) => {
    const snapshot = staffSnapshot(values);
    // Nothing new since the last successful write (e.g. a retry after a
    // services failure) — don't re-send the staff record.
    if (savedSnapshotRef.current === snapshot && !photo.change) return;
    if (!staffIdRef.current && invitedRef.current) {
      throw new MissingStaffIdError();
    }
    const photoField = await resolvePhotoField();
    if (staffIdRef.current) {
      await updateStaffMember(values, photoField);
    } else {
      await inviteStaffMember(values, photoField);
    }
    savedSnapshotRef.current = snapshot;
    if (photoField) photo.markSaved(photoField.staff_photo_s3_uri);
  };

  /** Applies the Menu diff; returns whether anything was written. */
  const saveServices = async (values: StaffFormValues): Promise<boolean> => {
    const staffId = staffIdRef.current;
    if (!canWriteMenu) return false;
    if (!staffId) {
      const wantsServices = Object.values(values.serviceAssignments).some(
        (assignment) => assignment.enabled,
      );
      if (wantsServices) throw new MissingStaffIdError();
      return false;
    }
    const persisted = (persistedRef.current ??=
      persistedAssignments(baselineAssignments));
    const desired = values.serviceAssignments;
    const { toCreate, toUpdate, toDelete } = diffStaffServices({
      baseline: persisted,
      desired,
      staffId,
      skipCreate: unconfirmedCreatesRef.current,
    });
    const serviceIdOf = (staffServiceId: string) =>
      Object.keys(persisted).find(
        (key) => persisted[key].id === staffServiceId,
      );

    if (toUpdate.length > 0) {
      await updateServices({ staff_services: toUpdate });
      servicesWrittenRef.current = true;
      toUpdate.forEach(({ id }) => {
        const serviceId = serviceIdOf(id);
        if (serviceId) persisted[serviceId] = { ...desired[serviceId], id };
      });
    }
    let unconfirmed = 0;
    if (toCreate.length > 0) {
      const response = await createServices({
        business_location_id: businessId,
        staff_services: toCreate,
      });
      servicesWrittenRef.current = true;
      const createdIds = createdStaffServiceIds(response);
      toCreate.forEach(({ salon_service_id }) => {
        const id = createdIds[salon_service_id];
        if (id) {
          persisted[salon_service_id] = { ...desired[salon_service_id], id };
        } else {
          unconfirmedCreatesRef.current.add(salon_service_id);
          unconfirmed += 1;
        }
      });
    }
    if (toDelete.length > 0) {
      const results = await Promise.allSettled(
        toDelete.map((id) => deleteStaffService(id)),
      );
      results.forEach((result, index) => {
        if (result.status !== "fulfilled") return;
        servicesWrittenRef.current = true;
        const serviceId = serviceIdOf(toDelete[index]);
        if (serviceId) delete persisted[serviceId];
      });
      const failure = results.find((result) => result.status === "rejected");
      if (failure) throw (failure as PromiseRejectedResult).reason;
    }
    if (unconfirmed > 0) throw new UnconfirmedStaffServicesError(unconfirmed);
    return toCreate.length + toUpdate.length + toDelete.length > 0;
  };

  const refreshServicesQuietly = () =>
    Promise.resolve(refreshServices?.()).catch((error: unknown) =>
      console.error("Error refreshing services:", error),
    );

  const submit = async (
    values: StaffFormValues,
  ): Promise<StaffFormSubmitOutcome> => {
    try {
      await saveStaff(values);
    } catch (error) {
      console.error("Error saving staff member:", error);
      return {
        ok: false,
        message: isMissingStaffIdError(error)
          ? MISSING_STAFF_ID_MESSAGE
          : getServerErrorMessage(error),
      };
    }

    try {
      await saveServices(values);
    } catch (error) {
      console.error("Error saving staff services:", error);
      if (servicesWrittenRef.current) await refreshServicesQuietly();
      if (isMissingStaffIdError(error)) {
        return {
          ok: false,
          section: "menu",
          message: MISSING_STAFF_ID_MESSAGE,
        };
      }
      const name = values.firstName.trim() || "This staff member";
      const unconfirmed = unconfirmedStaffServicesCount(error);
      if (unconfirmed !== null) {
        return {
          ok: false,
          section: "menu",
          message: `${name} was saved, but we couldn't confirm ${
            unconfirmed === 1
              ? "1 new service was"
              : `${unconfirmed} new services were`
          } added. Click Save to finish, then reopen them to check their Menu.`,
        };
      }
      const reason = getServerErrorMessage(error, "").replace(/\.$/, "");
      return {
        ok: false,
        section: "menu",
        message: `${name} was saved, but their services couldn't be updated.${
          reason ? ` ${reason}.` : ""
        } Click Save to retry.`,
      };
    }

    if (servicesWrittenRef.current) await refreshServicesQuietly();
    return {
      ok: true,
      result: {
        staffId: staffIdRef.current,
        isCreate,
        servicesChanged: servicesWrittenRef.current,
      },
    };
  };

  return { submit, staffExists };
}
